import { clearCustomerWidgetAuthToken } from "@redotech/redo-customer-portal-app/api";
import { SetAuthContext } from "@redotech/redo-customer-portal-app/contexts/auth";
import { CustomerContext } from "@redotech/redo-customer-portal-app/contexts/CustomerContext";
import { SettingsContext } from "@redotech/redo-customer-portal-app/contexts/settings";
import {
  SetUserContext,
  UserContext,
} from "@redotech/redo-customer-portal-app/contexts/user";
import { CustomizationContext } from "@redotech/redo-customer-portal-app/customization/customization-context";
import { resolveCustomizationImage } from "@redotech/redo-customer-portal-app/customization/resolve-customization-field";
import { useTranslation } from "@redotech/redo-i18n";
import { LocaleSelector } from "@redotech/redo-i18n/locale-selector";
import { type RedoCommandMenuItem } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { allowCustomerAccounts } from "@redotech/redo-web/utils/customer-account-utils";
import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getUserDisplayEmail, getUserDisplayName } from "../utils/user-display";
import { NavbarFontContext } from "./navbar-font-context";
import { OrderContext } from "./order";
import { VersionFourNavbarDisplay } from "./version-four-navbar-display";

enum MenuOption {
  MyOrders = "My orders",
  MyAccount = "My account",
  Logout = "Logout",
  MyWarranties = "My warranties",
}

export function VersionFourNavbar() {
  const { t } = useTranslation();
  const settings = useContext(SettingsContext);
  const customerContext = useContext(CustomerContext);
  const { customization } = useContext(CustomizationContext);
  const { fontFamily: navbarFontFamily } = useContext(NavbarFontContext);
  const order = useContext(OrderContext);
  const navigate = useNavigate();
  const user = useContext(UserContext);
  const setUser = useContext(SetUserContext);
  const setAuth_ = useContext(SetAuthContext);

  const logoUrl = resolveCustomizationImage(
    customization?.branding?.logoUrl,
    settings?.theme?.logo_image,
  );

  const primaryEmail = getUserDisplayEmail(user, customerContext);
  const customerName = getUserDisplayName(user, customerContext);

  const orderName = order?.shopify?.name;

  const menuItems: RedoCommandMenuItem[] = useMemo(() => {
    const items: RedoCommandMenuItem[] = [];

    if (user && !user.guestEmail && user.email) {
      items.push({
        id: MenuOption.MyOrders,
        text: t("My Orders"),
        onClick: () => navigate("/returns-portal/orders"),
      });
    }

    if (
      user &&
      !user.guestEmail &&
      user.email &&
      settings?.warranties?.enabled
    ) {
      items.push({
        id: MenuOption.MyWarranties,
        text: t("My Warranties"),
        onClick: () => navigate("/returns-portal/account/warranties"),
      });
    }

    if (
      allowCustomerAccounts(settings?.customerAccounts) &&
      !!customerContext &&
      primaryEmail
    ) {
      items.push({
        id: MenuOption.MyAccount,
        text: t("My Account"),
        onClick: () => navigate("/returns-portal/account"),
      });
    }

    items.push({
      id: MenuOption.Logout,
      text: t("Logout"),
      onClick: () => {
        // @ts-expect-error: strict-null-checks
        setAuth_(undefined);
        // @ts-expect-error: strict-null-checks
        setUser(undefined);
        clearCustomerWidgetAuthToken();
        navigate("/returns-portal/login");
      },
    });

    return items;
  }, [
    user,
    settings,
    customerContext,
    primaryEmail,
    t,
    navigate,
    setAuth_,
    setUser,
  ]);

  if (settings?.hideTrackingPageNavbar) {
    return null;
  }

  if (!user || user?.isMerchant) {
    return null;
  }

  const storeDomain =
    settings?.brandKit?.customerFacingDomain || settings?.storeUrl;

  return (
    <VersionFourNavbarDisplay
      customerEmail={primaryEmail}
      customerName={customerName}
      fontFamily={navbarFontFamily}
      hideNavigation={settings?.hideTrackingPageNavigation}
      leftContent={<LocaleSelector />}
      logoHref={`https://${storeDomain}`}
      logoUrl={logoUrl}
      menuItems={menuItems}
      orderName={orderName}
      storeName={settings?.theme?.merchantDisplayName || settings?.merchantName}
    />
  );
}
